import { Funder } from "@/types/FunderTypes";
import White from "../../../assets/images/FileAvatar.svg";
import CompanySVG from "../../../assets/images/defaultCompany.svg";
import UploadSVG from "../../../assets/images/upload.svg";
import UserIconSVG from "../../../assets/images/user-profile.svg";

import { Colors } from "@/enums/Colors";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { Investor } from "@/models/crunchbase/Investor";
import { Organization } from "@/models/crunchbase/Organization";
import { Person } from "@/models/crunchbase/Person";
import FunderService from "@/services/FunderService";
import { LazoFileType } from "@/types/LazoFileType";
import { Box, CircularProgress, SxProps, styled } from "@mui/material";
import React, { FC, useEffect, useRef, useState } from "react";

interface Props {
  funder?: Funder;
  entity?: Organization | Person | Investor;
  funderImg?: string;
  sx?: SxProps;
  company?: boolean;
  enableImgUpload?: boolean;
  small?: boolean;
  onChangeImg?: () => void;
  containerSx?: SxProps;
}

export const FunderImage: FC<Props> = ({ funder, entity, sx, funderImg, company, enableImgUpload, small, onChangeImg, containerSx }) => {
  const defaultImg = company ? CompanySVG.src : UserIconSVG.src;
  const actualImg = funder?.entity?.logoUrl ?? entity?.logoUrl ?? funderImg;

  const [imageSrc, setImageSrc] = useState(defaultImg);
  const [isVisible, setIsVisible] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const fileInputRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      if (imgRef.current) {
        observer.unobserve(imgRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isVisible && actualImg) {
      const img = new Image();
      img.src = actualImg;
      img.onload = () => setImageSrc(actualImg);
      img.onerror = () => setImageSrc(defaultImg);
    }
  }, [isVisible, actualImg, defaultImg]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    FunderService.uploadFunderImage(funder.externalId, LazoFileType.FUNDER_IMAGE, funder.type, file)
      .then((res) => {
        res.onSuccess(() => {
          onChangeImg();
          NotificationService.info("Image uploaded successfully");
        });

        res.onError((error) => {
          NotificationService.error("Error uploading image");
          console.log(error);
        });
      })
      .finally(() => setLoading(false));
  };

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  return (
    <Container sx={containerSx} small={small}>
      <StyledImg small={small} sx={sx} ref={imgRef} src={loading ? White.src : imageSrc} alt={`funder-${funder?.externalId}`} />
      {funder?.uuid && enableImgUpload && (
        <>
          <label htmlFor="funder-icon-button-file">
            <button type="button" onClick={handleButtonClick}>
              <img src={UploadSVG.src} alt="upload-icon" />
            </button>
            {loading && <CircularProgress color="info" size={25} />}
          </label>
          <input
            accept="image/*"
            style={{ display: "none" }}
            id="funder-icon-button-file"
            type="file"
            onChange={handleImageChange}
            ref={fileInputRef}
          />
        </>
      )}
    </Container>
  );
};

const StyledImg = styled("img")<{ small?: boolean }>(({ small }) => ({
  width: small ? "1.5rem" : "2rem",
  height: small ? "1.5rem" : "2rem",
  objectFit: "contain",
  border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  borderRadius: "0.25rem",
}));

const Container = styled(Box)<{ small?: boolean }>(({ small }) => ({
  position: "relative",
  "& > label > button": {
    cursor: "pointer",
    border: `${Colors.LIGHT_GRAY_DEDEE1} solid 1px`,
    borderRadius: "0.25rem",
    display: "none",
    backgroundColor: Colors.BUTTON_HOVER_PURPLE,
    width: small ? "1.5rem" : "2rem",
    height: small ? "1.5rem" : "2rem",
    top: "0",
    left: "0",
    padding: "0",
  },
  "& > label": {
    "& span": {
      position: "absolute",
      top: "0",
      left: "0",
      marginLeft: "0.1875rem",
      marginTop: "0.1875rem",
    },
  },
  "&:hover": {
    "& > label > button": {
      position: "absolute",
      display: "grid",
      placeItems: "center",
    },
  },
}));

export default FunderImage;
